import {Link, Navigate, useLocation, useSearchParams} from 'react-router';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LinkStyle} from '@ui/buttons/external-link';
import {RegisterPayload, useRegister} from '../requests/use-register';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '@ui/settings/use-settings';
import React, {Fragment, useContext} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import ReactPhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css";
import { Controller } from 'react-hook-form';
import { requestNotificationPermission } from '../requests/firebase-config';



interface Props {
  inviteType?: string;
  deviceId?: string;
}
export function RegisterPage({inviteType}: Props) {
  const {branding, registration, social} = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();

  const isRegisteringUsingInvite =
    pathname.includes('workspace') || !!inviteType;
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;
  const searchParamsMobileNumber = searchParams.get('mobile_number') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: {
      email: searchParamsEmail,
      mobile_number: searchParamsMobileNumber,
      password: 'password',
      password_confirmation: 'password',
    },
    shouldFocusError: false,
  });
  const register = useRegister(form);

  if (registration?.disable && !isRegisteringUsingInvite) {
    return <Navigate to="/" replace />;
  }

  let heading = <Trans message="Create a new account" />;
  if (isRegisteringUsingInvite) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  }

  const message = (
    <Trans
      values={{
        a: parts => (
          <Link className={LinkStyle} to="/">
            {parts}
          </Link>
        ),
      }}
      message="Already have an account? <a>Sign in.</a>"
    />
  );

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <Form
        form={form}
        // onSubmit={async payload => {
        //   const isValid = await verify();
        //   if (isValid) {
        //     register.mutate({
        //       ...payload,
        //       invite_type: inviteType,
        //     });
        //   }
        // }}

        onSubmit={async (payload) => {
            const isValid = await verify();
            if (isValid) {
              const deviceId = localStorage.getItem('deviceId') || crypto.randomUUID();
              localStorage.setItem('deviceId', deviceId);

              // Get FCM token (will be null if permission denied)
              const fcmToken = await requestNotificationPermission(
                () => console.warn("Notification permission denied"),
                (token) => console.log("FCM token received:", token)
              );

              register.mutate({
                ...payload,
                invite_type: inviteType,
                deviceId: deviceId,
                token: fcmToken || 'NO_FCM_TOKEN',
              }, {
                onSuccess: (response) => {
                  // Handle successful registration
                },
                onError: (error) => {
                  // Handle registration error
                }
              });
            }
        }}
      >
        <FormTextField
          className="mb-32"
          name="email"
          type="email"
          disabled={!!searchParamsEmail}
          label={<Trans message="Email" />}
          required
        />

        <label className="block text-sm font-medium text-gray-100 mb-30">
          <span className="mobile_label">
            <Trans message="Mobile Number" />
          </span>
          <Controller
              name="mobile_number"
              control={form.control}
              rules={{ required: "Mobile number is required" }}
              render={({ field, fieldState }) => (
                <div className="w-full mobile_number">
                  <ReactPhoneInput
                    {...field}
                    inputProps={{
                      ref: field.ref, // Explicitly forward the ref
                      name: field.name,
                    }}
                    country={"in"}
                    countryCodeEditable={false}
                    disabled={!!searchParamsMobileNumber}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                  {fieldState.error && (
                    <div className="text-xs mt-1 font-normal text-red-600" style={{ color: '#dc2626' }}  >
                    {fieldState.error.message}
                  </div>
                  )}
                </div>
              )}
            />
        </label>


        {auth?.registerFields ? <auth.registerFields /> : null}
        <PolicyCheckboxes />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={register.isPending || isVerifying}
        >
          <Trans message="Create account" />
        </Button>
        <SocialAuthSection
          dividerMessage={
            social?.compact_buttons ? (
              <Trans message="Or sign up with" />
            ) : (
              <Trans message="OR" />
            )
          }
        />
      </Form>
    </AuthLayout>
  );
}

function PolicyCheckboxes() {
  const {registration} = useSettings();
  const policies = registration?.policies;

  if (!policies?.length) return null;

  return (
    <div className="mb-32">
      <FormCheckbox name="accepted_policies" className="mb-4 block" required>
        <Trans
          message="I agree to the :policies."
          values={{
            policies: (
              <Fragment>
                {policies.map((policy, index) => (
                  <Fragment key={policy.id}>
                    {index > 0 &&
                      (index === policies.length - 1 ? ' and ' : ', ')}
                    <CustomMenuItem
                      unstyled
                      className={() => LinkStyle}
                      item={policy}
                    />
                  </Fragment>
                ))}
              </Fragment>
            ),
          }}
        />
      </FormCheckbox>
    </div>
  );
}
